/**
 * @module ui.panel
 * @version 6.0.0
 * @description Native <dialog> based panel with multiple position variants
 * 
 * @element liftr-panel
 * 
 * @fires liftr:panel:opened - Panel opened
 * @fires liftr:panel:closed - Panel closed
 * 
 * @attr {string} liftr-id - Panel identifier (required for external toggle targeting)
 * @attr {string} liftr-state - State tokens (open, disabled, dragging, closing)
 * @attr {string} liftr-settings - Position configuration
 * @attr {string} liftr-scroll-to - Optional: liftr-id of element to scroll to after close
 * 
 * Positions (liftr-settings):
 * - popup:         Centered overlay with backdrop (default)
 * - sheet:         Bottom sheet with backdrop (supports drag-to-close)
 * - sidebar-left:  Left sidebar with backdrop
 * - sidebar-right: Right sidebar with backdrop
 * - dropdown:      Anchored to trigger, transparent backdrop
 * - menu:          Anchored to trigger corner, transparent backdrop
 * 
 * CSS Custom Properties:
 * - --panel-enabled: true | false
 * - --panel-mode: popup | sheet | sidebar-left | sidebar-right | dropdown | menu
 * - --panel-sidebar-width: 32rem (default)
 * - --panel-origin: transform-origin for anchored panels (set by JS)
 * - --panel-slide: slide direction for dropdown (set by JS)
 * - --panel-drag-y: drag offset for sheet (set by JS during drag)
 * 
 * Template Structure:
 * <liftr-panel liftr-id="my-panel" liftr-settings="popup">
 *   <dialog>
 *     <div liftr-role="panel-drag"><!-- Optional drag handle for sheet --></div>
 *     <button liftr-role="panel-close">Close</button>
 *     <!-- Content -->
 *   </dialog>
 * </liftr-panel>
 * 
 * External Toggle:
 * <button liftr-role="panel-toggle" liftr-target="my-panel">Open</button>
 * 
 * Scroll-to after close:
 * <liftr-panel liftr-id="filter-panel" liftr-scroll-to="product-list-abc123">
 *   <!-- After close, scrolls to [liftr-id="product-list-abc123"] -->
 * </liftr-panel>
 * 
 * @changelog
 * - 6.0.0: FIX: Safari exit transitions — deferred dialog.close()
 *        - Safari removes dialog from top layer immediately on close()
 *        - New flow: closing state → CSS transition → dialog.close()
 *        - ESC key now routes through animated close flow
 *        - Re-open during closing aborts exit and keeps panel open
 *        - Respects prefers-reduced-motion (instant close)
 *        - Fallback timeout from getComputedStyle transitionDuration
 *
 * - 5.9.1: FIX: Guard against duplicate customElements.define()
 *        - Shopify asset URL cache-busting can cause duplicate module loads
 *        - Dynamic import from accordion sheet mode triggers re-registration
 *
 * - 5.9.0: Added liftr-scroll-to attribute for scroll-after-close
 *        - Smooth scrolls to target element (by liftr-id) after panel close
 *        - Only scrolls if target is not already in viewport
 *        - Respects prefers-reduced-motion (instant scroll)
 * 
 * - 5.8.0: v4.5+ COMPLIANCE
 *        - Added: LISTEN constant for external event subscription
 *        - Added: CSS_SCHEMA frozen constant for watchSettings
 *        - Changed: on() uses LISTEN.PANEL_CLOSE instead of string literal
 * 
 * - 5.7.0: FIX: Backdrop logic moved to open() (was dead code in #openModal)
 *        - FIX: Use element identity (this) instead of liftr-id for exclusion
 *        - Removed unused #openModal() method
 *        - Secondary sidebars now correctly get transparent backdrop
 * 
 * - 5.6.0: PUBLIC API COMPLIANCE
 *        - Refactored to use optional chaining guard pattern
 *        - isOpen getter now uses native dialog.open property
 *        - All public methods safe to call before mount()
 *        - Added get mode getter
 *        - Added get panelId getter
 * 
 * CHANGELOG v5.5.1:
 * - FIX: Force layout reflow before emitting opened event
 * - Prevents child components (liftr-scroll) from calculating with stale dimensions
 *
 * CHANGELOG v5.5.0:
 * - ADD: PROPERTIES constant for CSS custom properties
 * - ADD: SETTINGS.BACKDROP_NONE constant
 * - Refactored to use constants instead of string literals
 * 
 * CHANGELOG v5.4.0:
 * - ADD: Drag-to-close for sheet mode via panel-drag handle
 * - ADD: DRAGGING state during drag interaction
 * - ADD: Velocity-based close detection (fast swipe)
 * - ADD: Threshold-based close detection (30% drag distance)
 * - ADD: --panel-drag-y CSS variable for transform during drag
 * 
 * CHANGELOG v5.3.0:
 * - FIX: Single calculation for position AND animation (no more mismatch)
 * - Refactored to #calculateAnchoredPosition() + #applyPosition()
 * - Removed duplicate logic in #prepareAnimation()
 * 
 * CHANGELOG v5.2.0:
 * - Dropdown: Slide + Fade statt Scale (natürlicher)
 * - --panel-slide CSS Variable für Richtung
 * 
 * CHANGELOG v5.1.0:
 * - transform-origin via CSS Variable (--panel-origin) statt inline style
 * - Bessere Animation-Koordination mit CSS
 */

import { LiftrModule } from './core.module.js';
import { on, ATTRIBUTES } from './core.liftr.js';

/* ═══════════════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Attribute for scroll-to target after panel close */
const ATTR_SCROLL_TO = 'liftr-scroll-to';

/**
 * Role constants for DOM queries.
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  TOGGLE: 'panel-toggle',
  CLOSE:  'panel-close',
  DRAG:   'panel-drag',
  TITLE:  'panel-title'
});

/**
 * State tokens for liftr-state attribute.
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  OPEN:     'open',
  DISABLED: 'disabled',
  DRAGGING: 'dragging',
  CLOSING:  'closing'
});

/**
 * Event action names (auto-prefixed with liftr:panel:).
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  OPENED: 'opened',
  CLOSED: 'closed'
});

/**
 * External events this module listens to.
 * @type {Readonly<Record<string, string>>}
 */
const LISTEN = Object.freeze({
  PANEL_CLOSE: 'liftr:panel:close'
});

/**
 * Settings tokens for liftr-settings attribute.
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({
  POPUP:         'popup',
  SHEET:         'sheet',
  SIDEBAR_LEFT:  'sidebar-left',
  SIDEBAR_RIGHT: 'sidebar-right',
  DROPDOWN:      'dropdown',
  MENU:          'menu',
  BACKDROP_NONE: 'backdrop-none'
});

/**
 * CSS Custom Properties set by JavaScript.
 * Pattern: --{module}-{property}
 * @type {Readonly<Record<string, string>>}
 */
const PROPERTIES = Object.freeze({
  ORIGIN: '--panel-origin',
  SLIDE:  '--panel-slide',
  DRAG_Y: '--panel-drag-y'
});

/**
 * CSS Settings schema for watchSettings (internal only, not exported).
 * @type {Readonly<Record<string, { type: string, default: * }>>}
 */
const CSS_SCHEMA = Object.freeze({
  mode: { type: 'string', default: 'popup' }
});

/**
 * Type configuration lookup.
 * @type {Readonly<Record<string, { modal: boolean, anchored: boolean }>>}
 */
const TYPE_CONFIG = Object.freeze({
  [SETTINGS.POPUP]:         { modal: true,  anchored: false },
  [SETTINGS.SHEET]:         { modal: true,  anchored: false },
  [SETTINGS.SIDEBAR_LEFT]:  { modal: true,  anchored: false },
  [SETTINGS.SIDEBAR_RIGHT]: { modal: true,  anchored: false },
  [SETTINGS.DROPDOWN]:      { modal: false, anchored: true },
  [SETTINGS.MENU]:          { modal: false, anchored: true }
});

const DEFAULT_TYPE = SETTINGS.POPUP;

/**
 * Panel types that should have transparent backdrop when stacked.
 * @type {Readonly<string[]>}
 */
const NO_BACKDROP_WHEN_STACKED = Object.freeze([
  SETTINGS.SIDEBAR_LEFT, 
  SETTINGS.SIDEBAR_RIGHT
]);

/* ═══════════════════════════════════════════════════════════════════════════
 * COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════ */

class LiftrPanel extends LiftrModule {
  
  /* ─────────────────────────────────────────────────────────────────────────
   * STATIC PROPERTIES
   * ───────────────────────────────────────────────────────────────────────── */
  
  static NAME = 'panel';
  
  static DEFAULTS = {
    closeOnBackdrop: true,
    closeOnEscape: true,
    gap: 8,
    dragThreshold: 0.3,
    velocityThreshold: 0.5
  };
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE FIELDS
   * ───────────────────────────────────────────────────────────────────────── */
  
  #dialog = null;
  #panelId = null;
  #currentType = DEFAULT_TYPE;
  #triggerElement = null;
  #globalController = null;
  #anchoredController = null;
  
  // Drag state for sheet
  #dragHandle = null;
  #dragState = null;
  
  // Scroll-to target
  #scrollToId = null;
  
  // Deferred close (Safari exit transition fix)
  #closingController = null;
  #closingTimeout = null;
  
  /* ─────────────────────────────────────────────────────────────────────────
   * LIFECYCLE
   * ───────────────────────────────────────────────────────────────────────── */
  
  mount() {
    this.#panelId = this.getAttribute(ATTRIBUTES.ID);
    this.#scrollToId = this.getAttribute(ATTR_SCROLL_TO) || null;
    this.state.remove(STATES.OPEN);
    
    this.#setupDialog();
    this.#setupDragHandle();
    this.#attachGlobalListeners();
    
    // Listen for programmatic close
    this.addDisposable(
      on(LISTEN.PANEL_CLOSE, (e) => {
        if (e.detail?.id === this.#panelId) {
          this.close();
        }
      })
    );
    
    // Watch CSS settings (enabled + mode)
    this.watchSettings('panel', {
      enabled: '--panel-enabled',
      css: CSS_SCHEMA,
      resize: true
    }, {
      onEnable: () => {},
      onDisable: () => this.close(),
      onChange: (changes) => {
        if (changes.initial) {
          this.#setMode(changes.css.mode);
          return;
        }
        if (changes.css?.mode) {
          this.#onModeChange(changes.css.mode);
        }
      }
    });
  }
  
  unmount() {
    // Abort pending close
    this.#abortClosing();
    
    // Close if open (direct, no animation)
    if (this.#dialog?.open) {
      this.#dialog.close();
    }
    
    this.#globalController?.abort();
    this.#globalController = null;
    
    this.#anchoredController?.abort();
    this.#anchoredController = null;
    
    this.#dragState = null;
    this.#scrollToId = null;
    
    super.unmount();
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PUBLIC API
   * ───────────────────────────────────────────────────────────────────────── */
  
  /**
   * Open the panel.
   * Safe to call before mount() - will silently return.
   * If called during exit animation, aborts close and keeps panel open.
   * @param {HTMLElement|null} trigger - The element that triggered the open
   */
  open(trigger = null) {

    // Re-open during exit animation: abort close, keep panel open
    if (this.state.has(STATES.CLOSING)) {
      this.#abortClosing();
      return;
    }

    // Guard: safe before mount()
    if (this.isOpen) return;
    if (!this.#dialog) return;
    
    this.#triggerElement = trigger;
    
    // Re-read CSS mode before opening (container may now have dimensions)
    this.#refreshModeFromCSS();
    
    const config = TYPE_CONFIG[this.#currentType] || TYPE_CONFIG[DEFAULT_TYPE];
    
    // BACKDROP: Secondary sidebars get transparent backdrop
    if (config.modal) {
      const allOpenPanels = document.querySelectorAll(
        `liftr-panel[${ATTRIBUTES.STATE}~="${STATES.OPEN}"]`
      );
      const hasOtherOpen = Array.from(allOpenPanels).some(p => p !== this);
      
      this.settings.toggle(
        SETTINGS.BACKDROP_NONE, 
        hasOtherOpen && NO_BACKDROP_WHEN_STACKED.includes(this.#currentType)
      );
    }
    
    // Clear any inline positioning from previous anchored opens
    if (!config.anchored) {
      this.#dialog.style.cssText = '';
    }
    
    // Open with correct method based on type
    if (config.anchored) {
      this.#openAnchored();
    } else {
      this.#dialog.showModal();
    }
    
    if (!this.#dialog.open) return;
    
    this.state.add(STATES.OPEN);
    this.#focusFirst();
    
    this.emit(EVENTS.OPENED, {
      id: this.#panelId,
      type: this.#currentType
    });

    this.#dialog.querySelectorAll('liftr-scroll').forEach(s => void s.offsetHeight);
  }
  
  /**
   * Close the panel with exit animation.
   * Safari-compatible: sets closing state, waits for CSS transition,
   * then calls native dialog.close().
   * Safe to call before mount() - will silently return.
   */
  close() {
    // Guard: safe before mount(), not open, or already closing
    if (!this.isOpen || this.state.has(STATES.CLOSING)) return;
    
    this.#anchoredController?.abort();
    this.#anchoredController = null;
    
    // Reset drag state
    this.#dragState = null;
    this.state.remove(STATES.DRAGGING);
    this.#dialog?.style.removeProperty(PROPERTIES.DRAG_Y);
    
    // Set closing state — triggers CSS exit transition
    // Dialog stays [open] + in top layer until transition completes
    this.state.add(STATES.CLOSING);
    
    // Reduced motion: skip transition, close immediately
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishClose();
      return;
    }
    
    // Wait for CSS transition to complete, then close dialog
    this.#closingController = new AbortController();
    const { signal } = this.#closingController;
    
    this.#dialog?.addEventListener('transitionend', (e) => {
      // Only react to dialog itself, not children
      if (e.target === this.#dialog) this.#finishClose();
    }, { signal });
    
    // Fallback timeout (parsed from CSS, clamped 100–1000ms)
    const duration = this.#getTransitionDuration();
    this.#closingTimeout = setTimeout(() => this.#finishClose(), duration + 50);
  }
  
  /**
   * Toggle the panel.
   * @param {HTMLElement|null} trigger - The element that triggered the toggle
   */
  toggle(trigger = null) {
    this.isOpen ? this.close() : this.open(trigger);
  }
  
  /**
   * Check if panel is open.
   * @type {boolean}
   */
  get isOpen() {
    return this.#dialog?.open ?? false;
  }
  
  /**
   * Current panel mode/position.
   * @type {string}
   */
  get mode() {
    return this.#currentType;
  }
  
  /**
   * Panel identifier.
   * @type {string|null}
   */
  get panelId() {
    return this.#panelId;
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Deferred Close (Safari Exit Transition Fix)
   * ───────────────────────────────────────────────────────────────────────── */
  
  /**
   * Complete the close: remove closing state and call native dialog.close().
   * @private
   */
  #finishClose() {
    // Prevent double execution (transitionend + timeout race)
    if (!this.state.has(STATES.CLOSING)) return;
    
    this.#closingController?.abort();
    this.#closingController = null;
    clearTimeout(this.#closingTimeout);
    this.#closingTimeout = null;
    
    // Remove closing state before dialog.close()
    this.state.remove(STATES.CLOSING);
    
    // Native close — fires 'close' event → #onDialogClose()
    this.#dialog?.close();
  }
  
  /**
   * Abort a pending close (e.g. re-open during exit animation).
   * @private
   */
  #abortClosing() {
    this.#closingController?.abort();
    this.#closingController = null;
    clearTimeout(this.#closingTimeout);
    this.#closingTimeout = null;
    this.state.remove(STATES.CLOSING);
  }
  
  /**
   * Parse transition-duration from dialog's computed style.
   * @returns {number} Duration in milliseconds (clamped 100–1000)
   * @private
   */
  #getTransitionDuration() {
    if (!this.#dialog) return 500;
    const raw = getComputedStyle(this.#dialog).transitionDuration;
    const match = raw?.match(/([\d.]+)\s*(s|ms)/);
    if (!match) return 500;
    const value = parseFloat(match[1]);
    const ms = match[2] === 's' ? value * 1000 : value;
    return Math.max(100, Math.min(1000, ms));
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Setup
   * ───────────────────────────────────────────────────────────────────────── */
  
  #setupDialog() {
    this.#dialog = this.querySelector(':scope > dialog');
    
    if (!this.#dialog) {
      throw new Error('[panel] Missing <dialog> element');
    }
    
    // Dialog close event (handles .close() calls)
    const handleClose = () => this.#onDialogClose();
    this.#dialog.addEventListener('close', handleClose);
    this.addDisposable(() => this.#dialog?.removeEventListener('close', handleClose));
    
    // Backdrop click
    const handleClick = (e) => this.#onDialogClick(e);
    this.#dialog.addEventListener('click', handleClick);
    this.addDisposable(() => this.#dialog?.removeEventListener('click', handleClick));
    
    // Close buttons
    const handleCloseClick = (e) => {
      const closeBtn = e.target.closest(`[${ATTRIBUTES.ROLE}~="${ROLES.CLOSE}"]`);
      if (!closeBtn) return;
      
      const closestDialog = closeBtn.closest('dialog');
      if (closestDialog === this.#dialog) {
        e.stopPropagation();
        this.close();
      }
    };
    this.#dialog.addEventListener('click', handleCloseClick);
    this.addDisposable(() => this.#dialog?.removeEventListener('click', handleCloseClick));
    
    // ESC key: always intercept, route through animated close
    const handleCancel = (e) => {
      e.preventDefault();
      if (this.config.closeOnEscape) {
        this.close();
      }
    };
    this.#dialog.addEventListener('cancel', handleCancel);
    this.addDisposable(() => this.#dialog?.removeEventListener('cancel', handleCancel));
  }
  
  #setupDragHandle() {
    this.#dragHandle = this.role(ROLES.DRAG);
    
    if (!this.#dragHandle) return;
    
    const handleTouchStart = (e) => this.#onDragStart(e);
    const handleTouchMove = (e) => this.#onDragMove(e);
    const handleTouchEnd = (e) => this.#onDragEnd(e);
    
    this.#dragHandle.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    
    this.addDisposable(() => {
      this.#dragHandle?.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    });
  }
  
  #attachGlobalListeners() {
    this.#globalController = new AbortController();
    const { signal } = this.#globalController;
    
    if (this.#panelId) {
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest(
          `[${ATTRIBUTES.ROLE}~="${ROLES.TOGGLE}"][${ATTRIBUTES.TARGET}="${this.#panelId}"]`
        );
        if (toggle) {
          e.preventDefault();
          this.toggle(toggle);
        }
      }, { signal });
    }
    
    window.addEventListener('pageshow', (e) => {
      if (e?.persisted && this.isOpen) {
        this.close();
      }
    }, { signal });
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Mode Management (CSS is SSOT)
   * ───────────────────────────────────────────────────────────────────────── */
  
  #setMode(mode) {
    if (!TYPE_CONFIG[mode]) {
      mode = DEFAULT_TYPE;
    }
    
    this.#currentType = mode;
    
    for (const t of Object.values(SETTINGS)) {
      if (t === SETTINGS.BACKDROP_NONE) continue;
      this.settings.remove(t);
    }
    this.settings.add(mode);
  }
  
  #refreshModeFromCSS() {
    const styles = getComputedStyle(this);
    const mode = styles.getPropertyValue('--panel-mode').trim() || DEFAULT_TYPE;
    
    if (TYPE_CONFIG[mode] && mode !== this.#currentType) {
      this.#setMode(mode);
    }
  }

  #onModeChange(newMode) {
    if (!TYPE_CONFIG[newMode]) {
      newMode = DEFAULT_TYPE;
    }
    
    if (this.#currentType === newMode) return;
    
    const wasOpen = this.isOpen;
    const wasAnchored = TYPE_CONFIG[this.#currentType]?.anchored;
    const isAnchored = TYPE_CONFIG[newMode]?.anchored;
    
    this.#setMode(newMode);
    
    if (wasOpen && wasAnchored !== isAnchored) {
      const trigger = this.#triggerElement;
      this.close();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.open(trigger);
        });
      });
    } else if (wasOpen && isAnchored) {
      const position = this.#calculateAnchoredPosition();
      this.#applyPosition(position);
    }
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Anchored Opening (Dropdown/Menu)
   * ───────────────────────────────────────────────────────────────────────── */
  
  #openAnchored() {
    if (!this.#triggerElement) {
      this.#dialog?.showModal();
      return;
    }
    
    const position = this.#calculateAnchoredPosition();
    
    this.#dialog?.style.setProperty(PROPERTIES.ORIGIN, position.transformOrigin);
    if (this.#currentType === SETTINGS.DROPDOWN) {
      this.#dialog?.style.setProperty(PROPERTIES.SLIDE, `${position.slideDirection}px`);
    }
    
    this.#dialog?.showModal();
    this.#applyPosition(position);
    this.#attachAnchoredListeners();
  }
  
  #calculateAnchoredPosition() {
    const triggerRect = this.#triggerElement.getBoundingClientRect();
    const { gap } = this.config;
    
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };
    
    const estimatedWidth = this.#currentType === SETTINGS.DROPDOWN ? 320 : 256;
    const estimatedHeight = this.#currentType === SETTINGS.DROPDOWN ? 384 : 320;
    
    const spaceBelow = viewport.height - triggerRect.bottom - gap;
    const spaceAbove = triggerRect.top - gap;
    
    const opensBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;
    
    let top, transformOrigin, slideDirection;
    
    if (opensBelow) {
      top = triggerRect.bottom + gap;
      transformOrigin = 'top';
      slideDirection = 8;
    } else {
      top = triggerRect.top - estimatedHeight - gap;
      transformOrigin = 'bottom';
      slideDirection = -8;
    }
    
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const isLeft = triggerCenterX < viewport.width / 2;
    
    let left = null;
    let right = null;
    
    if (this.#currentType === SETTINGS.DROPDOWN) {
      left = triggerRect.left;
      if (left + estimatedWidth > viewport.width - gap) {
        left = viewport.width - estimatedWidth - gap;
      }
      if (left < gap) left = gap;
    } else {
      if (isLeft) {
        left = triggerRect.left;
        if (left < gap) left = gap;
      } else {
        right = viewport.width - triggerRect.right;
        if (right < gap) right = gap;
      }
    }
    
    transformOrigin += isLeft ? ' left' : ' right';
    
    return {
      top,
      left,
      right,
      transformOrigin,
      slideDirection,
      triggerWidth: triggerRect.width,
      gap
    };
  }
  
  #applyPosition(position) {
    if (!this.#dialog) return;
    
    Object.assign(this.#dialog.style, {
      position: 'fixed',
      top: `${position.top}px`,
      left: position.left !== null ? `${position.left}px` : 'auto',
      right: position.right !== null ? `${position.right}px` : 'auto',
      bottom: 'auto',
      margin: '0',
      width: 'max-content',
      minWidth: `${position.triggerWidth}px`,
      maxWidth: `calc(100vw - ${position.gap * 2}px)`,
      maxHeight: `calc(100dvh - ${position.gap * 2}px)`
    });
  }
  
  #attachAnchoredListeners() {
    this.#anchoredController = new AbortController();
    const { signal } = this.#anchoredController;
    
    window.addEventListener('scroll', () => this.close(), { signal, passive: true });
    window.addEventListener('resize', () => this.close(), { signal, passive: true });
    
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) {
        this.close();
      }
    }, { threshold: 0 });
    
    observer.observe(this.#triggerElement);
    signal.addEventListener('abort', () => observer.disconnect());
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Drag Handling (Sheet only)
   * ───────────────────────────────────────────────────────────────────────── */
  
  #onDragStart(e) {
    if (this.#currentType !== SETTINGS.SHEET || !this.isOpen) return;
    
    const touch = e.touches[0];
    if (!touch) return;
    
    this.#dragState = {
      startY: touch.clientY,
      startTime: Date.now(),
      currentY: touch.clientY
    };
    
    this.state.add(STATES.DRAGGING);
    this.#dialog?.style.setProperty(PROPERTIES.DRAG_Y, '0px');
  }
  
  #onDragMove(e) {
    if (!this.#dragState) return;
    
    const touch = e.touches[0];
    if (!touch) return;
    
    const deltaY = touch.clientY - this.#dragState.startY;
    const clampedDelta = Math.max(0, deltaY);
    
    this.#dragState.currentY = touch.clientY;
    this.#dialog?.style.setProperty(PROPERTIES.DRAG_Y, `${clampedDelta}px`);
    
    if (clampedDelta > 0) {
      e.preventDefault();
    }
  }
  
  #onDragEnd(e) {
    if (!this.#dragState) return;
    
    const { startY, startTime, currentY } = this.#dragState;
    const deltaY = Math.max(0, currentY - startY);
    const elapsed = Date.now() - startTime;
    
    const velocity = elapsed > 0 ? deltaY / elapsed : 0;
    const dialogHeight = this.#dialog?.offsetHeight ?? 0;
    const dragRatio = dialogHeight > 0 ? deltaY / dialogHeight : 0;
    
    const shouldClose = 
      velocity >= this.config.velocityThreshold ||
      dragRatio >= this.config.dragThreshold;
    
    this.#dragState = null;
    this.state.remove(STATES.DRAGGING);
    
    if (shouldClose) {
      this.close();
    } else {
      this.#dialog?.style.setProperty(PROPERTIES.DRAG_Y, '0px');
    }
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Scroll After Close
   * ───────────────────────────────────────────────────────────────────────── */
  
  #scrollToTarget() {
      if (!this.#scrollToId) return;
      
      const target = document.querySelector(
        `[${ATTRIBUTES.ID}="${this.#scrollToId}"]`
      );
      if (!target) return;
      
      const section = target.closest('.shopify-section') || target;
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      section.scrollIntoView({
        behavior: prefersReduced ? 'instant' : 'smooth',
        block: 'start'
      });
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Event Handlers
   * ───────────────────────────────────────────────────────────────────────── */
  
  #onDialogClose() {
    this.state.remove(STATES.OPEN);
    this.state.remove(STATES.CLOSING);
    this.state.remove(STATES.DRAGGING);
    
    this.#anchoredController?.abort();
    this.#anchoredController = null;
    
    this.#dialog?.style.removeProperty(PROPERTIES.DRAG_Y);
    
    if (this.#triggerElement) {
      try {
        this.#triggerElement.focus();
      } catch {
        // Silent fail
      }
    }
    this.#triggerElement = null;
    
    this.emit(EVENTS.CLOSED, {
      id: this.#panelId,
      type: this.#currentType
    });
    
    requestAnimationFrame(() => this.#scrollToTarget());
  }
  
  #onDialogClick(e) {
    if (e.target === this.#dialog && this.config.closeOnBackdrop) {
      this.close();
    }
  }
  
  /* ─────────────────────────────────────────────────────────────────────────
   * PRIVATE - Focus Management
   * ───────────────────────────────────────────────────────────────────────── */
  
  static #FOCUSABLE = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');
  
  #focusFirst() {
    requestAnimationFrame(() => {
      if (this.#dialog?.querySelector('[autofocus]')) return;

      const title = this.role(ROLES.TITLE);
      if (title) {
        if (!title.hasAttribute('tabindex')) title.setAttribute('tabindex', '-1');
        title.focus();
        return;
      }

      const first = this.#dialog?.querySelector(LiftrPanel.#FOCUSABLE);
      first?.focus();
    });
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════ */

if (!customElements.get('liftr-panel')) {
  customElements.define('liftr-panel', LiftrPanel);
}

export { LiftrPanel, ROLES, STATES, EVENTS, SETTINGS, PROPERTIES };
